import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Download, Mail, Trash2, Undo2 } from 'lucide-react';
import { api, APIError } from '../api/client';
import { API_BASE_URL } from '../config/api';
import { getAuthToken } from '../utils/auth';

/**
 * GDPR rights surface for the C2C profile screen.
 *
 *   - PATCH /api/me/consents       → marketing opt-in / opt-out
 *   - GET   /api/me/export         → JSON download of all personal data
 *   - DELETE /api/me               → schedule hard delete with 30-day grace
 *   - POST  /api/me/cancel-deletion → undo while still in grace
 *
 * Self-contained: pulls /api/me on mount, owns local state. Drop into
 * ProfileScreen with no props.
 */
export default function PrivacyDataSection() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const reload = useCallback(async () => {
    try {
      const data = await api.get('/api/me');
      setMe(data);
      setError('');
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message;
      setError(typeof detail === 'string' ? detail : 'Could not load privacy controls.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const setMarketingConsent = async (next) => {
    setBusy(true); setError(''); setSuccess('');
    try {
      const updated = await api.patch('/api/me/consents', { marketing_consent: next });
      setMe(updated);
      setSuccess(next ? 'Marketing emails enabled.' : 'Marketing emails turned off.');
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message;
      setError(typeof detail === 'string' ? detail : 'Could not update preference.');
    } finally {
      setBusy(false);
    }
  };

  const downloadData = async () => {
    setBusy(true); setError(''); setSuccess('');
    try {
      const token = getAuthToken();
      const resp = await fetch(`${API_BASE_URL}/api/me/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename = `jobskills-data-${(me?.id || 'me').slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.json`;
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setSuccess('Your data was downloaded as a JSON file.');
    } catch (err) {
      setError(err.message || 'Could not download data.');
    } finally {
      setBusy(false);
    }
  };

  const scheduleDeletion = async () => {
    setBusy(true); setError(''); setSuccess('');
    try {
      const resp = await api.delete('/api/me');
      setSuccess(resp.message || 'Account scheduled for deletion.');
      setConfirmingDelete(false);
      await reload();
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message;
      setError(typeof detail === 'string' ? detail : 'Could not schedule deletion.');
    } finally {
      setBusy(false);
    }
  };

  const cancelDeletion = async () => {
    setBusy(true); setError(''); setSuccess('');
    try {
      await api.post('/api/me/cancel-deletion');
      setSuccess('Deletion request cancelled — your account is safe.');
      await reload();
    } catch (err) {
      const detail = err instanceof APIError ? (err.data?.detail || err.message) : err.message;
      setError(typeof detail === 'string' ? detail : 'Could not cancel deletion.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  const deletionPending = Boolean(me?.deletion_scheduled_at);
  const graceEndsLabel = deletionPending
    ? new Date(me.deletion_scheduled_at).toLocaleDateString(undefined, {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null;

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5">
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Privacy & data</h2>
        <p className="text-xs text-gray-500 mt-1">
          Manage your consent, download your data, or delete your account under
          GDPR Articles 7, 15, and 17.
        </p>
      </header>

      {error ? (
        <div className="mb-3 rounded-xl border border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>
      ) : null}
      {success ? (
        <div className="mb-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">{success}</div>
      ) : null}

      {deletionPending ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">
                Your account is scheduled for deletion on {graceEndsLabel}.
              </p>
              <p className="text-xs text-amber-800 mt-1">
                After that date your account, profile, and interview history will be
                permanently erased. You can cancel any time before then.
              </p>
              <button
                type="button"
                onClick={cancelDeletion}
                disabled={busy}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              >
                <Undo2 className="w-3.5 h-3.5" />
                Cancel deletion
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Marketing consent toggle */}
      <div className="flex items-start justify-between gap-4 py-3 border-t border-gray-100">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900 flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-500" />
            Marketing emails
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Product updates, interview tips, and career insights. Unsubscribe anytime.
          </p>
        </div>
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={Boolean(me?.marketing_consent_active)}
            disabled={busy}
            onChange={(e) => setMarketingConsent(e.target.checked)}
          />
          <span className="w-10 h-6 bg-gray-200 rounded-full relative peer-checked:bg-orange-500 transition">
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                me?.marketing_consent_active ? 'translate-x-4' : ''
              }`}
            />
          </span>
        </label>
      </div>

      {/* Data export */}
      <div className="flex items-start justify-between gap-4 py-3 border-t border-gray-100">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-900 flex items-center gap-2">
            <Download className="w-4 h-4 text-gray-500" />
            Download my data
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Get a JSON file with everything we have on you (Article 15 GDPR).
          </p>
        </div>
        <button
          type="button"
          onClick={downloadData}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-gray-50 disabled:opacity-50"
        >
          Export
        </button>
      </div>

      {/* Delete account */}
      {!deletionPending ? (
        <div className="py-3 border-t border-gray-100">
          <p className="text-sm font-medium text-slate-900 flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-red-600" />
            Delete my account
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Permanent erasure after a 30-day grace window. You can still sign in
            and cancel during that time.
          </p>
          {!confirmingDelete ? (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              disabled={busy}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Delete account
            </button>
          ) : (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3">
              <p className="text-xs text-red-900">
                Are you sure? Your interview history, profile, and saved data
                will be permanently deleted on day 31.
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={scheduleDeletion}
                  disabled={busy}
                  className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {busy ? 'Scheduling…' : 'Yes, schedule deletion'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={busy}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
